import { BookConstants } from '@/constants/bookConstants';
import { gsap } from 'gsap';
import type { FederatedPointerEvent } from 'pixi.js';
import { PixiBookRenderer } from './PixiBookRenderer';
import {
  calculateSideEdgeShadowProgress,
  pickAdaptiveSideEdgeCorner,
  reflectCornerAcrossCrease
} from './sideEdgeDrag';
import type { HoverCornerKey, FlipCorner, Point } from '@/types/book';

const {
  HOVER_PREVIEW_DELAY_MS,
  CLICK_HOVER_SUPPRESS_MS,
  CLICK_FLIP_DISTANCE_PX,
  MIDDLE_DRAG_ENGAGE_PX,
  MIDDLE_DRAG_EDGE_INSET_PX,
  MIDDLE_DRAG_INTRO_MS,
  HOVER_PREVIEW_INTRO_MS
} = BookConstants;

export class FlipController {
  private readonly renderer: PixiBookRenderer;
  private dragging = false;
  private hoverPreviewing = false;
  private hoverPreviewTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingHoverPoint: Point | null = null;
  private pendingHoverCorner: HoverCornerKey | null = null;
  private activeHoverCorner: HoverCornerKey | null = null;
  private hoverSuppressedCorner: HoverCornerKey | null = null;
  private hoverSuppressTimer: ReturnType<typeof setTimeout> | null = null;
  private pointerDownPoint: Point | null = null;
  private pointerDownClientPoint: Point | null = null;
  private pointerDownCorner: HoverCornerKey | null = null;
  /** 按下点是否位于四角操作区：仅四角允许静止点击自动翻页。 */
  private pointerDownOnCorner = false;
  private lastPointerPoint: Point | null = null;
  /** 侧边中间“预备态”：已按下但尚未往内拖过阈值，未真正起翻。 */
  private middleArmed = false;
  private middleArmPoint: Point | null = null;
  /** 侧边拖动时，真实纸张外边缘上的抓取点。 */
  private middleGrabPoint: Point | null = null;
  /** 侧边拖动当前使用的真实纸角；拖动中可在上下约束之间稳定切换。 */
  private middleCorner: FlipCorner | null = null;
  /**
   * 中央拖拽起翻的“滑入”过渡动画：折页从外缘滑到按下点，期间由该 tween 驱动 foldTo，
   * 非空表示滑入进行中，此时忽略实时指针移动、只更新 lastPointerPoint 作为滑入终点。
   */
  private middleIntroTween: gsap.core.Tween | null = null;
  /**
   * hover 预览的“滑入”过渡动画：折页从近角滑到光标点，期间由该 tween 驱动 foldTo，
   * 非空表示滑入进行中，此时忽略实时 hover 跟随、只更新 hoverIntroTargetPoint 作为终点。
   */
  private hoverIntroTween: gsap.core.Tween | null = null;
  private hoverIntroTargetPoint: Point | null = null;
  private enabled = true;
  private manualFlipBlocked = false;
  private clickFlipEnabled = true;

  /** 获取 Pixi 画布 DOM，用于非 passive 的滚轮监听 */
  private get canvasElement(): HTMLElement {
    const app = this.renderer.options.app;
    return (app.renderer.events.domElement as HTMLElement | null) ?? app.canvas;
  }

  /** 滚轮监听选项：passive 必须为 false，否则无法阻止页面滚动 */
  private static readonly wheelListenerOptions: AddEventListenerOptions = {
    passive: false,
    capture: true
  };

  /** 画布 pointerdown 使用捕获阶段，确保书外空白区域也能收到按下事件。 */
  private static readonly pointerDownListenerOptions: AddEventListenerOptions = {
    capture: true
  };

  /** 绑定画布 DOM 的指针与滚轮事件，覆盖书外空白与书内全部翻页入口。 */
  constructor(_renderer: PixiBookRenderer) {
    this.renderer = _renderer;
    this.renderer.root.on('pointermove', this.onRootPointerMove);
    this.renderer.root.on('pointerleave', this.onRootPointerLeave);
    this.canvasElement.addEventListener(
      'pointerdown',
      this.onCanvasPointerDown,
      FlipController.pointerDownListenerOptions
    );
    this.canvasElement.addEventListener('wheel', this.onWheel, FlipController.wheelListenerOptions);
  }

  /** 解绑所有交互事件，释放控制器持有的监听。 */
  destroy(): void {
    this.renderer.root.off('pointermove', this.onRootPointerMove);
    this.renderer.root.off('pointerleave', this.onRootPointerLeave);
    this.canvasElement.removeEventListener(
      'pointerdown',
      this.onCanvasPointerDown,
      FlipController.pointerDownListenerOptions.capture
    );
    this.canvasElement.removeEventListener(
      'wheel',
      this.onWheel,
      FlipController.wheelListenerOptions.capture
    );
    this.cancelHoverPreviewTimer();
    this.clearHoverSuppression();
    this.killMiddleIntro();
    this.killHoverIntro();
    this.stopWindowTracking();
  }

  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;

    this.enabled = enabled;

    if (enabled) return;

    this.cancelManualInteraction();
  }

  setManualFlipBlocked(blocked: boolean): void {
    if (this.manualFlipBlocked === blocked) return;

    this.manualFlipBlocked = blocked;
    if (blocked) this.cancelManualInteraction();
  }

  setClickFlipEnabled(enabled: boolean): void {
    this.clickFlipEnabled = enabled;
  }

  private canHandleManualFlip(): boolean {
    return this.enabled && !this.manualFlipBlocked;
  }

  private cancelManualInteraction(): void {
    if (this.dragging) {
      this.dragging = false;
      this.killMiddleIntro();
      this.stopWindowTracking();
      this.renderer.stopFold('user');
      this.clearMiddleDragState();
      this.clearPointerDownState();
    }

    if (this.middleArmed) {
      this.endMiddleArm();
    }

    this.clearHoverSuppression();
    this.stopHoverPreview();
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /** 处理画布按下：四角立即起翻；侧边中间/外侧空白进入预备态，等待往内拖过阈值再起翻。 */
  private readonly onCanvasPointerDown = (event: PointerEvent): void => {
    if (!this.canHandleManualFlip() || !event.isPrimary || event.button !== 0) return;

    const point = this.renderer.clientToBook(event.clientX, event.clientY);
    const onCorner = this.renderer.canStartAt(point);

    // 非四角：侧边中间带或书外左右空白可进入预备态。
    if (!onCorner && !this.hoverPreviewing) {
      if (this.renderer.canStartBlankSideDragAt(point)) this.armMiddleDrag(point);
      return;
    }

    // 四角 / 悬停预览续接：沿用原有立即起翻逻辑（手指=外角，无偏移）。
    this.cancelHoverPreviewTimer();
    this.pointerDownPoint = point;
    this.pointerDownClientPoint = { x: event.clientX, y: event.clientY };
    this.pointerDownCorner = this.hoverPreviewing
      ? this.activeHoverCorner
      : this.getHoverCorner(point);
    this.pointerDownOnCorner = onCorner || this.hoverPreviewing;
    this.lastPointerPoint = point;
    if (this.hoverPreviewing) {
      // hover 转拖拽：中断滑入过渡，交回拖拽实时驱动，避免 tween 与拖拽同时 foldTo。
      this.killHoverIntro();
      this.hoverIntroTargetPoint = null;
      this.renderer.setFoldCenteringEnabled(true);
    }
    this.dragging =
      this.hoverPreviewing || this.renderer.startFold(point, undefined, undefined, 'user');
    this.hoverPreviewing = false;
    this.activeHoverCorner = null;
    if (this.dragging) {
      this.renderer.beginManualFlip();
      this.startWindowTracking();
      this.renderer.foldTo(point);
    }
  };

  /** 中央按下：记录预备态并开始窗口级追踪，等待往内位移越过阈值后再真正起翻。 */
  private armMiddleDrag(point: Point): void {
    this.cancelHoverPreviewTimer();
    this.middleArmed = true;
    this.middleArmPoint = point;
    this.pointerDownPoint = point;
    this.pointerDownClientPoint = null;
    this.pointerDownCorner = this.getHoverCorner(point);
    this.pointerDownOnCorner = false;
    this.lastPointerPoint = point;
    this.startWindowTracking();
  }

  /**
   * 侧边预备态越过阈值：按当前落点分区起翻。
   * 落在四角 → 角拖拽；落在侧边中间 → 把真实纸边抓取点映射到鼠标。
   * 单页模式：翻页方向由水平拖拽手势决定（左拖下一页、右拖上一页），与按下点相对中线无关。
   */
  private engageMiddleDrag(currentPoint: Point): void {
    const start = this.middleArmPoint;
    this.middleArmed = false;
    this.middleArmPoint = null;
    if (!start) return;

    if (this.renderer.canStartAt(currentPoint)) {
      this.engageCornerDrag(currentPoint);
      return;
    }

    const rect = this.renderer.getRect();
    const isSingle = this.renderer.options.displayMode === 'single';
    // 步骤1：解析翻页方向——单页看拖拽水平方向，双页看按下点左右半区
    const swipeDirection: 'forward' | 'back' =
      currentPoint.x < start.x ? 'forward' : 'back';
    const pressDirection: 'forward' | 'back' =
      start.x < rect.width / 2 ? 'back' : 'forward';
    const logicalDirection = isSingle ? swipeDirection : pressDirection;

    // 步骤2：拖拽朝向不可翻方向时直接提示（双页封面在右半区右滑也能提示首页）
    if (!this.renderer.canFlip(swipeDirection)) {
      this.renderer.emitBoundFlipAttempt(swipeDirection);
      this.stopWindowTracking();
      this.clearPointerDownState();
      return;
    }

    // 步骤3：抓取对应方向的真实纸边
    const edgeX = logicalDirection === 'forward' ? rect.width : 0;
    const grabPoint = { x: edgeX, y: start.y };
    const corner = pickAdaptiveSideEdgeCorner(
      grabPoint,
      currentPoint,
      rect.pageWidth,
      rect.height,
      null
    );

    // 步骤4：起翻失败时透传 user，便于首页/末页边界提示
    if (
      !this.renderer.startFold(
        {
          x: start.x,
          y: corner === 'top' ? 0 : rect.height
        },
        undefined,
        logicalDirection,
        'user'
      )
    ) {
      this.stopWindowTracking();
      this.clearPointerDownState();
      return;
    }

    this.middleGrabPoint = grabPoint;
    this.middleCorner = corner;
    this.dragging = true;
    this.renderer.beginManualFlip();

    // 按下点离外缘较远（页面中部起翻）时，先用 0.3s 过渡把折页从外缘滑入按下点，避免突兀；
    // 已贴近外缘则沿用瞬时起翻（首帧用按下点，避免 x 已在页内中部导致折页瞬间跳到半开）。
    const introEdgeX = this.renderer.getMiddleDragIntroEdgeX(start, logicalDirection);
    if (introEdgeX === null) {
      this.foldMiddleTo(currentPoint);
    } else {
      this.startMiddleIntro(introEdgeX, currentPoint);
    }
  }

  /**
   * 折页从外缘滑入到当前指针位置的过渡动画：t∈[0,1] 期间终点随手指更新，
   * 结束时正好落在指针处、无跳变，随后交回实时拖拽驱动。
   */
  private startMiddleIntro(edgeX: number, initialPointer: Point): void {
    this.killMiddleIntro();
    const grabPoint = this.middleGrabPoint;
    if (!grabPoint) return;
    const inward = grabPoint.x < this.renderer.getRect().width / 2 ? 1 : -1;
    const introStart = {
      x: edgeX + inward * MIDDLE_DRAG_EDGE_INSET_PX,
      y: grabPoint.y
    };
    // 同步渲染滑入首帧（外缘平铺贴边）：startFold 只重排了图层但未跑 applyCalculation，
    // 若等到 tween 的下一帧才首次 foldTo，中间会渲染出一帧“生”状态（翻起页平铺、
    // staticLayerMask 未建立、底页未定位）而闪烁。这里先补上首帧再启动动画。
    this.foldMiddleTo(introStart);
    const state = { t: 0 };
    this.middleIntroTween = gsap.to(state, {
      t: 1,
      duration: MIDDLE_DRAG_INTRO_MS / 1000,
      ease: 'power3.out',
      onUpdate: () => {
        const target = this.lastPointerPoint ?? initialPointer;
        const finger = {
          x: introStart.x + (target.x - introStart.x) * state.t,
          y: introStart.y + (target.y - introStart.y) * state.t
        };
        this.foldMiddleTo(finger);
      },
      onComplete: () => {
        this.middleIntroTween = null;
        if (this.dragging && this.lastPointerPoint) {
          this.foldMiddleTo(this.lastPointerPoint);
        }
      }
    });
  }

  /** 中断并清理正在进行的滑入过渡动画。 */
  private killMiddleIntro(): void {
    if (!this.middleIntroTween) return;
    this.middleIntroTween.kill();
    this.middleIntroTween = null;
  }

  /** 角拖拽起翻：折页点跟随指针，不做 Y 投影。 */
  private engageCornerDrag(point: Point): void {
    if (!this.renderer.startFold(point, undefined, undefined, 'user')) {
      this.stopWindowTracking();
      this.clearPointerDownState();
      return;
    }

    this.clearMiddleDragState();
    this.dragging = true;
    this.renderer.beginManualFlip();
    this.renderer.foldTo(point);
  }

  /** 将侧边抓取的鼠标位置换算成真实纸角位置；物理约束由 FlipCalculator 处理。 */
  private foldPointFor(point: Point): Point {
    if (!this.middleGrabPoint || !this.middleCorner) return point;
    if (this.renderer.isHardCoverFoldActive()) return point;
    const rect = this.renderer.getRect();
    const corner = pickAdaptiveSideEdgeCorner(
      this.middleGrabPoint,
      point,
      rect.pageWidth,
      rect.height,
      this.middleCorner
    );
    if (corner !== this.middleCorner) {
      this.middleCorner = corner;
      this.renderer.setFoldCorner(corner);
    }
    const cornerPoint = {
      x: this.middleGrabPoint.x,
      y: corner === 'top' ? 0 : rect.height
    };
    return reflectCornerAcrossCrease(cornerPoint, this.middleGrabPoint, point);
  }

  private foldMiddleTo(point: Point): void {
    const grabPoint = this.middleGrabPoint;
    const shadowProgress = grabPoint
      ? calculateSideEdgeShadowProgress(grabPoint, point, this.renderer.getRect().pageWidth)
      : undefined;
    this.renderer.foldTo(this.foldPointFor(point), shadowProgress);
  }

  private clearMiddleDragState(): void {
    this.middleGrabPoint = null;
    this.middleCorner = null;
  }

  /** 处理窗口级移动事件，把浏览器坐标同步给翻页渲染器。 */
  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.canHandleManualFlip()) return;
    const point = this.renderer.clientToBook(event.clientX, event.clientY);

    if (this.dragging) {
      this.lastPointerPoint = point;
      // 滑入过渡进行中：折页由 tween 驱动并以 lastPointerPoint 作为终点，跳过实时 foldTo。
      if (this.middleIntroTween) return;
      this.foldMiddleTo(point);
      return;
    }

    // 侧边预备态：越过位移阈值才真正起翻；单页按水平拖拽量，双页优先往书内拖。
    // 朝不可翻方向水平拖（如封面右滑）也按水平位移起手，以便弹出边界提示。
    if (this.middleArmed && this.middleArmPoint) {
      this.lastPointerPoint = point;
      const moved = Math.hypot(point.x - this.middleArmPoint.x, point.y - this.middleArmPoint.y);
      const horizontal = Math.abs(point.x - this.middleArmPoint.x);
      const swipeDirection: 'forward' | 'back' =
        point.x < this.middleArmPoint.x ? 'forward' : 'back';
      const towardBound = !this.renderer.canFlip(swipeDirection);
      const progress =
        this.renderer.options.displayMode === 'single' || towardBound
          ? horizontal
          : this.getInwardDistance(this.middleArmPoint, point);
      if (moved > MIDDLE_DRAG_ENGAGE_PX && progress > MIDDLE_DRAG_ENGAGE_PX * 0.4) {
        this.engageMiddleDrag(point);
      }
    }
  };

  /** 处理抬起或取消事件，结束当前拖拽并触发回弹或翻页完成动画。 */
  private readonly onPointerUp = (event: PointerEvent): void => {
    if (!this.canHandleManualFlip()) return;

    // 中央静止点击（未越过阈值）：不翻页、不建折页图层，零副作用。
    if (this.middleArmed) {
      this.endMiddleArm();
      return;
    }

    if (!this.dragging) return;
    this.dragging = false;
    this.killMiddleIntro();
    this.stopWindowTracking();
    if (this.shouldAutoFlipOnClick(event)) {
      this.autoFlipFromCorner(this.pointerDownCorner);
    } else {
      this.renderer.stopFold('user');
    }
    this.clearMiddleDragState();
    this.clearPointerDownState();
  };

  private readonly onPointerCancel = (): void => {
    if (!this.canHandleManualFlip()) return;

    if (this.middleArmed) {
      this.endMiddleArm();
      return;
    }

    if (!this.dragging) return;
    this.dragging = false;
    this.killMiddleIntro();
    this.stopWindowTracking();
    this.clearMiddleDragState();
    this.renderer.stopFold('user');
    this.clearPointerDownState();
  };

  /** 结束中央预备态（未起翻的抬起/取消）。 */
  private endMiddleArm(): void {
    this.middleArmed = false;
    this.middleArmPoint = null;
    this.stopWindowTracking();
    this.clearPointerDownState();
  }

  // 步骤3：滚轮翻页处理：往下滚下一页，往上滚上一页（四个翻页角区域不触发）
  private readonly onWheel = (event: WheelEvent): void => {
    if (!this.canHandleManualFlip()) return;
    if (this.dragging || this.renderer.isAnimating()) return;

    const point = this.renderer.clientToBook(event.clientX, event.clientY);
    // 鼠标在翻页角区域内时不触发滚轮翻页，留给拖拽操作
    if (this.renderer.canStartAt(point)) return;

    event.preventDefault();

    const flipCorner: FlipCorner = point.y < this.renderer.getRect().height / 2 ? 'top' : 'bottom';

    if (event.deltaY > 0) {
      this.renderer.next(flipCorner, false, 'user');
    } else if (event.deltaY < 0) {
      this.renderer.prev(flipCorner, false, 'user');
    }
  };

  /** 在拖拽期间监听窗口级指针事件，避免光标离开画布后丢失拖拽。 */
  private readonly onRootPointerMove = (event: FederatedPointerEvent): void => {
    if (!this.canHandleManualFlip()) return;
    if (this.dragging || this.middleArmed || this.renderer.isAnimating()) return;

    const point = this.renderer.globalToBook(event);
    const corner = this.renderer.canStartAt(point) ? this.getHoverCorner(point) : null;

    if (!corner) {
      this.clearHoverSuppression();
      this.stopHoverPreview();
      return;
    }
    if (corner === this.hoverSuppressedCorner) return;
    this.hoverSuppressedCorner = null;

    if (this.hoverPreviewing) {
      if (corner !== this.activeHoverCorner) {
        this.stopHoverPreview();
        this.scheduleHoverPreview(point, corner);
        return;
      }

      this.hoverIntroTargetPoint = point;
      // 滑入过渡进行中：折页由 tween 驱动并以 hoverIntroTargetPoint 作为终点，跳过实时 foldTo。
      if (this.hoverIntroTween) return;
      this.renderer.foldTo(point);
      return;
    }

    this.scheduleHoverPreview(point, corner);
  };

  private readonly onRootPointerLeave = (): void => {
    if (!this.canHandleManualFlip()) return;
    if (this.dragging) return;
    this.clearHoverSuppression();
    this.stopHoverPreview();
  };

  private scheduleHoverPreview(point: Point, corner: HoverCornerKey): void {
    this.pendingHoverPoint = point;
    if (this.pendingHoverCorner === corner && this.hoverPreviewTimer) return;

    this.cancelHoverPreviewTimer();
    this.pendingHoverPoint = point;
    this.pendingHoverCorner = corner;
    this.hoverPreviewTimer = setTimeout(this.startHoverPreview, HOVER_PREVIEW_DELAY_MS);
  }

  private readonly startHoverPreview = (): void => {
    const point = this.pendingHoverPoint;
    const corner = this.pendingHoverCorner;
    this.hoverPreviewTimer = null;
    this.pendingHoverPoint = null;
    this.pendingHoverCorner = null;

    if (
      !point ||
      !corner ||
      !this.canHandleManualFlip() ||
      this.dragging ||
      this.renderer.isAnimating() ||
      !this.renderer.canStartAt(point)
    )
      return;

    this.hoverPreviewing = this.renderer.startFold(point);
    if (!this.hoverPreviewing) return;

    this.activeHoverCorner = corner;
    this.renderer.setFoldCenteringEnabled(false);
    this.startHoverIntro(point);
  };

  /**
   * hover 预览折页从近角滑入到当前光标点：t∈[0,1] 期间终点随光标更新，
   * 结束时正好落在光标处、无跳变，随后交回实时 hover 跟随。
   */
  private startHoverIntro(point: Point): void {
    this.killHoverIntro();
    this.hoverIntroTargetPoint = point;
    const start = this.renderer.getCornerIntroStart(point);
    // 同步渲染滑入首帧，避免 startFold 重排图层后到 tween 首帧之间的空档闪烁。
    this.renderer.foldTo(start);
    const state = { t: 0 };
    this.hoverIntroTween = gsap.to(state, {
      t: 1,
      duration: HOVER_PREVIEW_INTRO_MS / 1000,
      ease: 'power3.out',
      onUpdate: () => {
        const target = this.hoverIntroTargetPoint ?? point;
        this.renderer.foldTo({
          x: start.x + (target.x - start.x) * state.t,
          y: start.y + (target.y - start.y) * state.t
        });
      },
      onComplete: () => {
        this.hoverIntroTween = null;
        if (this.hoverPreviewing && this.hoverIntroTargetPoint) {
          this.renderer.foldTo(this.hoverIntroTargetPoint);
        }
      }
    });
  }

  /** 中断并清理正在进行的 hover 滑入过渡动画。 */
  private killHoverIntro(): void {
    if (!this.hoverIntroTween) return;
    this.hoverIntroTween.kill();
    this.hoverIntroTween = null;
  }

  private stopHoverPreview(): void {
    this.cancelHoverPreviewTimer();
    this.killHoverIntro();
    this.hoverIntroTargetPoint = null;
    if (!this.hoverPreviewing) return;

    this.hoverPreviewing = false;
    this.activeHoverCorner = null;
    this.renderer.stopFold();
  }

  private cancelHoverPreviewTimer(): void {
    if (!this.hoverPreviewTimer) return;

    clearTimeout(this.hoverPreviewTimer);
    this.hoverPreviewTimer = null;
    this.pendingHoverPoint = null;
    this.pendingHoverCorner = null;
  }

  private getHoverCorner(point: Point): HoverCornerKey {
    const rect = this.renderer.getRect();
    const horizontal = point.x < rect.width / 2 ? 'left' : 'right';
    const vertical = point.y < rect.height / 2 ? 'top' : 'bottom';

    return `${horizontal}-${vertical}` as HoverCornerKey;
  }

  /**
   * 计算从侧边起点往书内拖动的水平位移（书本坐标 px）。
   * 左侧起点向右为正，右侧起点向左为正。
   */
  private getInwardDistance(start: Point, current: Point): number {
    const rect = this.renderer.getRect();
    const onLeft = start.x < rect.width / 2;
    const dx = current.x - start.x;
    return onLeft ? dx : -dx;
  }

  private shouldAutoFlipOnClick(event: PointerEvent): boolean {
    // 仅四角的静止点击才自动翻页；中央静止点击一律不翻。
    if (!this.clickFlipEnabled) return false;
    if (!this.pointerDownOnCorner) return false;
    if (!this.pointerDownPoint || !this.pointerDownCorner) return false;

    if (this.pointerDownClientPoint) {
      return (
        Math.hypot(
          event.clientX - this.pointerDownClientPoint.x,
          event.clientY - this.pointerDownClientPoint.y
        ) <= CLICK_FLIP_DISTANCE_PX
      );
    }

    const releasePoint =
      event.clientX === undefined || event.clientY === undefined
        ? this.lastPointerPoint
        : this.renderer.clientToBook(event.clientX, event.clientY);
    if (!releasePoint) return false;

    return (
      Math.hypot(
        releasePoint.x - this.pointerDownPoint.x,
        releasePoint.y - this.pointerDownPoint.y
      ) <= CLICK_FLIP_DISTANCE_PX
    );
  }

  private autoFlipFromCorner(corner: HoverCornerKey | null): void {
    if (!corner) return;

    const flipCorner: FlipCorner = corner.endsWith('top') ? 'top' : 'bottom';
    const autoFlipDuration = corner.startsWith('right')
      ? this.renderer.next(flipCorner, false, 'user')
      : this.renderer.prev(flipCorner, false, 'user');

    this.suppressHoverAfterClick(corner, autoFlipDuration ?? 0);
  }

  private clearPointerDownState(): void {
    this.pointerDownPoint = null;
    this.pointerDownClientPoint = null;
    this.pointerDownCorner = null;
    this.pointerDownOnCorner = false;
    this.lastPointerPoint = null;
  }

  private suppressHoverAfterClick(corner: HoverCornerKey, autoFlipDuration: number): void {
    this.clearHoverSuppression();
    this.hoverSuppressedCorner = corner;
    this.hoverSuppressTimer = setTimeout(() => {
      this.hoverSuppressedCorner = null;
      this.hoverSuppressTimer = null;
    }, autoFlipDuration + CLICK_HOVER_SUPPRESS_MS);
  }

  private clearHoverSuppression(): void {
    if (this.hoverSuppressTimer) {
      clearTimeout(this.hoverSuppressTimer);
      this.hoverSuppressTimer = null;
    }
    this.hoverSuppressedCorner = null;
  }

  private startWindowTracking(): void {
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerCancel);
  }

  /** 停止窗口级指针监听，避免重复绑定和内存泄漏。 */
  private stopWindowTracking(): void {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerCancel);
  }
}
